/*
  bare slider (product cards and review cards)
  - Arrows move one card.
  - The card list is cloned so the row loops endlessly in both directions.
  - Optional autoplay advances one card every few seconds; it pauses on hover, focus,
    touch, arrow use and hidden tabs, and is off for visitors who prefer reduced motion.
*/
if (!customElements.get('bare-product-slider')) {
  customElements.define(
    'bare-product-slider',
    class BareProductSlider extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.bare-products__track, [data-slider-track]');
        if (!this.track) return;
        this.originals = Array.from(this.track.children);
        if (this.originals.length < 2) return;

        this.buttons = Array.from(this.querySelectorAll('.bare-products__arrow, [data-slider-arrow]'));
        this.buttons.forEach((b) => b.addEventListener('click', () => this.onArrow(parseInt(b.dataset.dir, 10))));

        this.paused = 0; // count of active pause reasons
        this.addClones();
        this.track.addEventListener('scroll', () => this.onScroll(), { passive: true });
        window.addEventListener('resize', () => this.addClones());

        const delay = parseInt(this.dataset.autoplay, 10);
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (delay && !reduce) {
          this.delay = delay;
          this.holdWhile(this, 'mouseenter', 'mouseleave');
          this.addEventListener('focusin', (e) => {
            if (e.target.matches(':focus-visible')) this.rest(8000);
          });
          this.track.addEventListener('pointerdown', () => this.rest(6000), { passive: true });
          this.track.addEventListener('wheel', () => this.rest(6000), { passive: true });
          document.addEventListener('visibilitychange', () => (document.hidden ? this.stop() : this.play()));
          this.play();
        }
      }

      disconnectedCallback() {
        this.stop();
      }

      // Width of one full set of cards, including the gap before the first clone.
      setWidth() {
        const first = this.originals[0];
        const clone = this.track.children[this.originals.length];
        return clone ? clone.offsetLeft - first.offsetLeft : this.track.scrollWidth;
      }

      // Keep adding copies of the cards until the row is long enough to loop without a gap.
      addClones() {
        let guard = 0;
        while (guard++ < 6) {
          const sets = this.track.children.length / this.originals.length;
          if (sets >= 2 && this.track.scrollWidth - this.setWidth() >= this.track.clientWidth + this.step()) break;
          this.originals.forEach((card) => {
            const copy = card.cloneNode(true);
            copy.setAttribute('aria-hidden', 'true');
            copy.setAttribute('inert', '');
            copy.removeAttribute('data-shopify-editor-block');
            this.track.appendChild(copy);
          });
        }
      }

      step() {
        const card = this.originals[0];
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        return card.getBoundingClientRect().width + gap;
      }

      // Instantly shift by one set width (invisible, because the clones are identical).
      jump(by) {
        this.track.classList.add('is-jumping');
        this.track.scrollLeft += by;
        void this.track.offsetWidth;
        this.track.classList.remove('is-jumping');
      }

      normalise() {
        const w = this.setWidth();
        if (this.track.scrollLeft >= w) this.jump(-w);
      }

      onScroll() {
        clearTimeout(this.settle);
        this.settle = setTimeout(() => this.normalise(), 120);
      }

      move(dir) {
        if (dir < 0 && this.track.scrollLeft < this.step() - 2) this.jump(this.setWidth());
        this.track.scrollBy({ left: dir * this.step(), behavior: 'smooth' });
      }

      onArrow(dir) {
        this.move(dir);
        this.rest(8000);
      }

      holdWhile(el, on, off) {
        let held = false;
        el.addEventListener(on, () => {
          if (!held) { held = true; this.paused++; }
        });
        el.addEventListener(off, () => {
          if (held) { held = false; this.paused = Math.max(0, this.paused - 1); }
        });
      }

      // Pause autoplay for a while after the visitor interacts.
      rest(ms) {
        this.restUntil = Date.now() + ms;
      }

      play() {
        if (!this.delay) return;
        this.stop();
        this.timer = setInterval(() => {
          if (this.paused || (this.restUntil && Date.now() < this.restUntil)) return;
          this.move(1);
        }, this.delay);
      }

      stop() {
        clearInterval(this.timer);
      }
    }
  );
}
